import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';

import { useStopExperiment } from '../../hooks/use-experiment-stop';
import type { ExperimentStopFinalStatus } from '../../services/experiment-stop.service';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  experimentId: string;
  /** Context-specific copy overrides — e.g. the List Archive refusal explains
   *  why a stop is needed before archiving. Defaults to the header-CTA copy. */
  title?: string;
  description?: string;
  /** Runs after a successful stop (the dialog has already closed). */
  onStopped?: () => void;
}

/**
 * Confirmation modal for `POST /experiments/{id}/stop`. The operator picks
 * the terminal outcome — CANCELLED (abort, results incomplete) or COMPLETED
 * (mark as finished early when enough signal has accrued). Per the
 * State-Transitions spec these are the only two terminal values reachable
 * via `/stop`; both are irreversible (server returns 409 on subsequent
 * /stop calls). Mutation toast handling lives in the hook.
 */
export function StopExperimentDialog({
  open,
  onOpenChange,
  experimentId,
  title,
  description,
  onStopped,
}: Props) {
  const { t } = useTranslation('experiments');
  const [finalStatus, setFinalStatus] = useState<ExperimentStopFinalStatus>('CANCELLED');
  const stop = useStopExperiment();

  const handleConfirm = () => {
    // Default reason for the header-Stop CTA — operator clicked the generic
    // Stop button without per-outcome context. Chatbot-action dispatch sends
    // its own outcome-specific reason via `ConfirmTerminalActionDialog`.
    const reason =
      finalStatus === 'CANCELLED'
        ? 'Operator stopped experiment via header CTA'
        : 'Operator marked experiment complete via header CTA';
    stop.mutate(
      { expId: experimentId, payload: { finalStatus, reason } },
      {
        onSuccess: () => {
          onOpenChange(false);
          onStopped?.();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title ?? t('detail.stop.title')}</DialogTitle>
          <DialogDescription>{description ?? t('detail.stop.description')}</DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={finalStatus}
          onValueChange={(v) => {
            setFinalStatus(v as ExperimentStopFinalStatus);
          }}
          className="gap-3"
        >
          <div className="flex items-start gap-2">
            <RadioGroupItem value="CANCELLED" id="stop-cancelled" className="mt-1 cursor-pointer" />
            <Label htmlFor="stop-cancelled" className="flex cursor-pointer flex-col gap-0.5">
              <span className="text-sm font-medium">{t('detail.stop.options.cancel.label')}</span>
              <span className="text-muted-foreground text-xs">
                {t('detail.stop.options.cancel.description')}
              </span>
            </Label>
          </div>
          <div className="flex items-start gap-2">
            <RadioGroupItem value="COMPLETED" id="stop-completed" className="mt-1 cursor-pointer" />
            <Label htmlFor="stop-completed" className="flex cursor-pointer flex-col gap-0.5">
              <span className="text-sm font-medium">{t('detail.stop.options.complete.label')}</span>
              <span className="text-muted-foreground text-xs">
                {t('detail.stop.options.complete.description')}
              </span>
            </Label>
          </div>
        </RadioGroup>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={stop.isPending}
            isLoading={stop.isPending}
            className="cursor-pointer"
          >
            {t('detail.stop.cancelButton')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={stop.isPending}
            isLoading={stop.isPending}
            className="cursor-pointer"
          >
            {t('detail.stop.confirmButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
